import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/**
 * M10 L10.09 — Review Audit Trail (§19).
 *
 * Read-only view of the immutable audit events recorded against a review,
 * fetched from `GET /v1/quality/reviews/:reviewId/audit` (T129).
 */
interface AuditRow {
  readonly auditId: string;
  readonly action: string;
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly detailJson: unknown;
  readonly createdAt: string;
}

export function QualityAuditPage(): ReactNode {
  const { reviewId = '' } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<readonly AuditRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiRequest<readonly AuditRow[]>(`/v1/quality/reviews/${reviewId}/audit`, { token });
      setRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [reviewId, token]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="quality-audit">
      <p style={{ marginBottom: 8 }}><Link to={`/quality/review/${reviewId}`}>{t('quality.common.back')}</Link></p>
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('quality.audit.title')}</h1>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {rows.length === 0 && !error ? (
        <p data-testid="audit-empty" style={{ color: 'var(--color-text-muted)' }}>{t('quality.audit.empty')}</p>
      ) : (
        <DataTable<AuditRow>
          testId="audit-table"
          rowKey={(r) => r.auditId}
          rowTestId={(r) => `audit-row-${r.auditId}`}
          columns={[
            { key: 'when', header: t('quality.audit.col.when'), render: (r) => at(r.createdAt) },
            { key: 'action', header: t('quality.audit.col.action'), render: (r) => r.action },
            { key: 'actor', header: t('quality.audit.col.actor'), render: (r) => personLabel(r.actorName, r.actorCode) ?? '—' },
            { key: 'detail', header: t('quality.audit.col.detail'), render: (r) => <code style={{ fontSize: 11 }}>{r.detailJson ? JSON.stringify(r.detailJson) : '—'}</code> },
          ]}
          rows={rows}
        />
      )}
    </main>
  );
}
